import React from "react";
import { Space } from "antd";
import { AxInput, AxIconWrapper, AxDivider, AxButton } from "@dynata/axiom3";

class Input extends React.Component {
  render() {
    return (
      <div className="section input" id="input">
        <h3>Input</h3>
        <p>Single line input element</p>
        <AxDivider orientation="left" plain>
          Basic Usage
        </AxDivider>
        <Space direction="vertical">
          <Space size="large" direction="vertical">
            <pre>{`<AxInput type="default" size="small" placeholder="Address" />`}</pre>
            <div className="prop-header">
              <p className="props-title">Props</p>
              <p>
                For AntD props, visit{" "}
                <AxButton
                  size="small"
                  type="link"
                  target="_blank"
                  href="https://ant.design/components/input/#API"
                >
                  https://ant.design/components/input/#API
                </AxButton>
              </p>
            </div>
            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>type</td>
                  <td>Predefined types for input</td>
                  <td>default | success | warning | error</td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td>placeholder</td>
                  <td>Same as HTML attribute</td>
                  <td>string</td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td>label</td>
                  <td>Same as HTML attribute</td>
                  <td>string</td>
                  <td></td>
                  <td></td>
                </tr>

                <tr>
                  <td>caption</td>
                  <td>Display error/info message</td>
                  <td>string</td>
                  <td></td>
                  <td></td>
                </tr>
                <tr>
                  <td>addonAfter</td>
                  <td>
                    The label text displayed after (on the right side of) the
                    input field
                  </td>
                  <td>ReactNode</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>addonBefore</td>
                  <td>
                    The label text displayed before (on the left side of) the
                    input field
                  </td>
                  <td>ReactNode</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>allowClear</td>
                  <td>If allow to remove input content with clear icon</td>
                  <td>
                    boolean | <pre>{"{ clearIcon: ReactNode }"}</pre>
                  </td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>bordered</td>
                  <td>Whether has border style</td>
                  <td>boolean</td>
                  <td>true</td>
                  <td>4.5.0</td>
                </tr>
                <tr>
                  <td>defaultValue</td>
                  <td>The initial input content</td>
                  <td>string</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>disabled</td>
                  <td>Whether the input is disabled</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>id</td>
                  <td>The ID for input</td>
                  <td>string</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>maxLength</td>
                  <td>The max length</td>
                  <td>number</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>showCount</td>
                  <td>Whether show text count</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td>4.18.0 info.value: 4.23.0</td>
                </tr>
                <tr>
                  <td>status</td>
                  <td>Set validation status</td>
                  <td>'error' | 'warning'</td>
                  <td>-</td>
                  <td>4.19.0</td>
                </tr>
                <tr>
                  <td>prefix</td>
                  <td>The prefix icon for the Input</td>
                  <td>ReactNode</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>size</td>
                  <td>
                    The size of the input box. Note: in the context of a form,
                    the <code>middle</code> size is used
                  </td>
                  <td>
                    <code>large</code> | <code>middle</code> |{" "}
                    <code>small</code>
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>suffix</td>
                  <td>The suffix icon for the Input</td>
                  <td>ReactNode</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>value</td>
                  <td>The input content value</td>
                  <td>string</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onChange</td>
                  <td>Callback when user input</td>
                  <td>function(e)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onPressEnter</td>
                  <td>
                    The callback function that is triggered when Enter key is
                    pressed
                  </td>
                  <td>function(e)</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>...others</td>
                  <td>
                    All extra props are passed down to the{" "}
                    <>
                      <AxButton
                        size="small"
                        type="link"
                        target="_blank"
                        href="https://4x.ant.design/components/input/#API"
                      >
                        {`<Input />`}
                      </AxButton>
                    </>{" "}
                    component.
                  </td>
                  <td></td>
                  <td>-</td>
                  <td></td>
                </tr>
              </tbody>
            </table>

            <pre>{`
<AxInput type="default" placeholder="Default" />

<AxInput
  type="default"
  placeholder="Ax Input"
  label="Input Label"
  caption="Input Message / Error"
/>

<AxInput
  type="default"
  placeholder="Input Disabled"
  label="Input Label"
  caption="Input Message / Error"
  disabled
/>

<AxInput
  type="default"
  placeholder="Icon Left"
  prefix={<i className="axiom-icon icon-check"></i>}
/>

<AxInput
  type="default"
  placeholder="Icon Right"
  suffix={<i className="axiom-icon icon-info-solid"></i>}
/>

<AxInput
  type="default"
  placeholder="Disabled with Icons"
  prefix={<i className="axiom-icon icon-info-solid"></i>}
  suffix={<i className="axiom-icon icon-check"></i>}
  disabled={true}
/>`}</pre>
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          Default | Danger | Positive | Warning
        </AxDivider>

        <Space size="large" direction align="start">
          <Space direction="vertical" size={16}>
            <AxInput type="default" size="small" placeholder="Default" />
            <AxInput type="default" size="medium" placeholder="Default" />
            <AxInput type="default" size="large" placeholder="Default" />
            <AxInput type="default" size="xlarge" placeholder="Default" />
            <AxInput
              type="default"
              prefix={
                <AxIconWrapper type="regular" size="14" icon={"fa-star"} />
              }
              suffix={
                <AxIconWrapper type="regular" size="14" icon={"fa-star"} />
              }
              placeholder="Default"
              disabled
            />

            <AxInput
              type="default"
              size="small"
              prefix={
                <AxIconWrapper type="regular" size="14" icon={"fa-star"} />
              }
              placeholder="Default"
              allowClear={true}
            />

            <AxInput
              type="default"
              size="medium"
              prefix={
                <AxIconWrapper type="regular" size="16" icon={"fa-star"} />
              }
              placeholder="Default"
              allowClear={true}
            />

            <AxInput
              type="default"
              size="medium"
              prefix={"prefix"}
              suffix={"suffix"}
              placeholder="Default"
              allowClear={true}
            />

            <AxInput
              type="default"
              size="medium"
              prefix={
                <AxIconWrapper type="regular" size="16" icon={"fa-star"} />
              }
              suffix={
                <AxIconWrapper type="regular" size="16" icon={"fa-star"} />
              }
              placeholder="Default"
              allowClear={true}
            />

            <AxInput
              type="default"
              size="large"
              prefix={
                <AxIconWrapper type="regular" size="18" icon={"fa-star"} />
              }
              placeholder="Default"
              allowClear={true}
            />

            <AxInput
              type="default"
              size="xlarge"
              prefix={
                <AxIconWrapper type="regular" size="18" icon={"fa-star"} />
              }
              placeholder="Default"
              allowClear={true}
            />
          </Space>

          <Space direction="vertical" size={16}>
            <AxInput type="danger" size="small" placeholder="Default" />
            <AxInput type="danger" size="medium" placeholder="Default" />
            <AxInput type="danger" size="large" placeholder="Default" />
            <AxInput type="danger" size="xlarge" placeholder="Default" />
            <AxInput
              type="danger"
              prefix={
                <AxIconWrapper type="regular" size="14" icon={"fa-star"} />
              }
              suffix={
                <AxIconWrapper type="regular" size="14" icon={"fa-star"} />
              }
              placeholder="Default"
              disabled
            />

            <AxInput
              type="danger"
              size="small"
              prefix={
                <AxIconWrapper type="regular" size="14" icon={"fa-star"} />
              }
              placeholder="Default"
            />

            <AxInput
              type="danger"
              size="medium"
              prefix={
                <AxIconWrapper type="regular" size="16" icon={"fa-star"} />
              }
              placeholder="Default"
            />

            <AxInput
              type="danger"
              size="medium"
              prefix={"prefix"}
              suffix={"suffix"}
              placeholder="Default"
            />

            <AxInput
              type="danger"
              size="medium"
              prefix={
                <AxIconWrapper type="regular" size="16" icon={"fa-star"} />
              }
              suffix={
                <AxIconWrapper type="regular" size="16" icon={"fa-star"} />
              }
              placeholder="Default"
            />

            <AxInput
              type="danger"
              size="large"
              prefix={
                <AxIconWrapper type="regular" size="18" icon={"fa-star"} />
              }
              placeholder="Default"
            />

            <AxInput
              type="danger"
              size="xlarge"
              prefix={
                <AxIconWrapper type="regular" size="18" icon={"fa-star"} />
              }
              placeholder="Default"
            />
          </Space>

          <Space direction="vertical" size={16}>
            <AxInput type="positive" size="small" placeholder="Default" />
            <AxInput type="positive" size="medium" placeholder="Default" />
            <AxInput type="positive" size="large" placeholder="Default" />
            <AxInput type="positive" size="xlarge" placeholder="Default" />
            <AxInput
              type="positive"
              prefix={
                <AxIconWrapper type="regular" size="14" icon={"fa-star"} />
              }
              suffix={
                <AxIconWrapper type="regular" size="14" icon={"fa-star"} />
              }
              placeholder="Default"
              disabled
            />

            <AxInput
              type="positive"
              size="small"
              prefix={
                <AxIconWrapper type="regular" size="14" icon={"fa-star"} />
              }
              placeholder="Default"
            />

            <AxInput
              type="positive"
              size="medium"
              prefix={
                <AxIconWrapper type="regular" size="16" icon={"fa-star"} />
              }
              placeholder="Default"
            />

            <AxInput
              type="positive"
              size="medium"
              prefix={"prefix"}
              suffix={"suffix"}
              placeholder="Default"
            />

            <AxInput
              type="positive"
              size="medium"
              prefix={
                <AxIconWrapper type="regular" size="16" icon={"fa-star"} />
              }
              suffix={
                <AxIconWrapper type="regular" size="16" icon={"fa-star"} />
              }
              placeholder="Default"
            />

            <AxInput
              type="positive"
              size="large"
              prefix={
                <AxIconWrapper type="regular" size="18" icon={"fa-star"} />
              }
              placeholder="Default"
            />

            <AxInput
              type="positive"
              size="xlarge"
              prefix={
                <AxIconWrapper type="regular" size="18" icon={"fa-star"} />
              }
              placeholder="Default"
            />
          </Space>

          <Space direction="vertical" size={16}>
            <AxInput type="warning" size="small" placeholder="Default" />
            <AxInput type="warning" size="medium" placeholder="Default" />
            <AxInput type="warning" size="large" placeholder="Default" />
            <AxInput type="warning" size="xlarge" placeholder="Default" />

            <AxInput
              type="warning"
              size="small"
              prefix={
                <AxIconWrapper type="regular" size="14" icon={"fa-star"} />
              }
              placeholder="Default"
            />

            <AxInput
              type="warning"
              size="medium"
              prefix={
                <AxIconWrapper type="regular" size="16" icon={"fa-star"} />
              }
              placeholder="Default"
            />

            <AxInput
              type="warning"
              size="medium"
              prefix={"prefix"}
              suffix={"suffix"}
              placeholder="Default"
            />

            <AxInput
              type="warning"
              size="medium"
              prefix={
                <AxIconWrapper type="regular" size="16" icon={"fa-star"} />
              }
              suffix={
                <AxIconWrapper type="regular" size="16" icon={"fa-star"} />
              }
              placeholder="Default"
            />

            <AxInput
              type="warning"
              size="large"
              prefix={
                <AxIconWrapper type="regular" size="18" icon={"fa-star"} />
              }
              placeholder="Default"
            />

            <AxInput
              type="warning"
              size="xlarge"
              prefix={
                <AxIconWrapper type="regular" size="18" icon={"fa-star"} />
              }
              placeholder="Default"
            />
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          With Label
        </AxDivider>

        <Space direction="vertical" size={16}>
          <pre>{`<AxInput
  type="default"
  size="small"
  placeholder="Default"
  label={
    <span>
      Label text{" "}
      <AxIconWrapper
        type="regular"
        size="14"
        icon={"fa-circle-info"}
      />
    </span>
  }
  caption="Caption text"
/>
<AxInput
  type="default"
  size="medium"
  placeholder="Default"
  label={
    <span>
      Label text{" "}
      <AxIconWrapper
        type="regular"
        size="14"
        icon={"fa-circle-info"}
      />
    </span>
  }
  caption="Caption text"
/>
<AxInput
  type="default"
  size="large"
  placeholder="Default"
  label={
    <span>
      Label text{" "}
      <AxIconWrapper
        type="regular"
        size="14"
        icon={"fa-circle-info"}
      />
    </span>
  }
  caption="Caption text"
/>
<AxInput
  type="default"
  size="xlarge"
  placeholder="Default"
  label={
    <span>
      Label text{" "}
      <AxIconWrapper
        type="regular"
        size="14"
        icon={"fa-circle-info"}
      />
    </span>
  }
  caption="Caption text"
/>`}</pre>
          <Space size={16}>
            <AxInput
              type="default"
              size="small"
              placeholder="Default"
              label={
                <span>
                  Label text{" "}
                  <AxIconWrapper
                    type="regular"
                    size="14"
                    icon={"fa-circle-info"}
                  />
                </span>
              }
              caption="Caption text"
            />
            <AxInput
              type="default"
              size="medium"
              placeholder="Default"
              label={
                <span>
                  Label text{" "}
                  <AxIconWrapper
                    type="regular"
                    size="14"
                    icon={"fa-circle-info"}
                  />
                </span>
              }
              caption="Caption text"
            />
            <AxInput
              type="default"
              size="large"
              placeholder="Default"
              label={
                <span>
                  Label text{" "}
                  <AxIconWrapper
                    type="regular"
                    size="14"
                    icon={"fa-circle-info"}
                  />
                </span>
              }
              caption="Caption text"
            />
            <AxInput
              type="default"
              size="xlarge"
              placeholder="Default"
              label={
                <span>
                  Label text{" "}
                  <AxIconWrapper
                    type="regular"
                    size="14"
                    icon={"fa-circle-info"}
                  />
                </span>
              }
              caption="Caption text"
            />
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          With Horizontal Label
        </AxDivider>

        <Space direction="vertical" size={16}>
          <pre>{`<AxInput
  type="default"
  size="small"
  placeholder="Default"
  direction="horizontal"
  label={
    <span>
      Long label text
      <AxIconWrapper
        type="regular"
        size="14"
        icon={"fa-circle-info"}
      />
    </span>
  }
  caption="Caption text"
/>

<AxInput
  type="default"
  size="medium"
  placeholder="Default"
  direction="horizontal"
  label={
    <span>
      Long label text
      <AxIconWrapper
        type="regular"
        size="14"
        icon={"fa-circle-info"}
      />
    </span>
  }
  caption="Caption text"
/>

<AxInput
  type="default"
  size="large"
  placeholder="Default"
  direction="horizontal"
  label={
    <span>
      Long label text
      <AxIconWrapper
        type="regular"
        size="14"
        icon={"fa-circle-info"}
      />
    </span>
  }
  caption="Caption text"
  required
/>

<AxInput
  type="default"
  size="xlarge"
  placeholder="Default"
  direction="horizontal"
  label={
    <span>
      Long label text
      <AxIconWrapper
        type="regular"
        size="14"
        icon={"fa-circle-info"}
      />
    </span>
  }
  caption="Caption text"
  required
/>`}</pre>
          <Space direction="vertical" size={24}>
            <AxInput
              type="default"
              size="small"
              placeholder="Default"
              direction="horizontal"
              label={
                <span>
                  Long label text
                  <AxIconWrapper
                    type="regular"
                    size="14"
                    icon={"fa-circle-info"}
                  />
                </span>
              }
              caption="Caption text"
            />

            <AxInput
              type="default"
              size="medium"
              placeholder="Default"
              direction="horizontal"
              label={
                <span>
                  Long label text
                  <AxIconWrapper
                    type="regular"
                    size="14"
                    icon={"fa-circle-info"}
                  />
                </span>
              }
              caption="Caption text"
            />

            <AxInput
              type="default"
              size="large"
              placeholder="Default"
              direction="horizontal"
              label={
                <span>
                  Long label text
                  <AxIconWrapper
                    type="regular"
                    size="14"
                    icon={"fa-circle-info"}
                  />
                </span>
              }
              caption="Caption text"
              required
            />

            <AxInput
              type="default"
              size="xlarge"
              placeholder="Default"
              direction="horizontal"
              label={
                <span>
                  Long label text
                  <AxIconWrapper
                    type="regular"
                    size="14"
                    icon={"fa-circle-info"}
                  />
                </span>
              }
              caption="Caption text"
              required
            />
          </Space>
        </Space>
      </div>
    );
  }
}
export default Input;
